import { getValidFontSize, getValidLetterSpacing, getValidLineHeight } from '@common/helpers/forms';

import type { ThemeCssSize, ThemeFont, ThemeResponsive } from '@sites/site-theme/src/entity/schemas';
import { ThemeCssUnit } from '@sites/site-theme/src/enums/css.enum';
import { THEME_FONT_PROPERTIES, type ThemeFontProperty } from '@sites/site-theme/src/helpers/font-css';
import type { ThemeBreakpoint } from '@sites/site-theme/src/helpers/responsive';
import { resolveBreakpoint } from '@sites/site-theme/src/helpers/responsive';

/**
 * The bridge between the stored font and the one the settings panel edits.
 *
 * The panel's inputs are numeric steppers — a font size is `16`, not `'16px'` — so the three sized
 * properties lose their unit on the way in and get it back on the way out. That conversion used to
 * be `helpers/converters.ts`, which also had to invert the breakpoint bags; now the shape matches
 * on both sides and only the unit is in question, which is the part a numeric input genuinely
 * cannot hold.
 */

/** The unit each sized property is edited in. Fixed per property, as the panel's inputs assume. */
const UNIT_BY_PROPERTY: Partial<Record<ThemeFontProperty, ThemeCssUnit>> = {
    fontSize: ThemeCssUnit.px,
    lineHeight: ThemeCssUnit.percent,
    letterSpacing: ThemeCssUnit.px,
};

/** Clamped on the way out, so a value typed past the input's bounds is not what gets stored. */
const CLAMP_BY_PROPERTY: Partial<Record<ThemeFontProperty, (value: number) => number>> = {
    fontSize: getValidFontSize,
    lineHeight: getValidLineHeight,
    letterSpacing: getValidLetterSpacing,
};

/** What the panel's controls hold: the same properties, with the sized ones as bare numbers. */
export type FontFormValue = {
    tag: ThemeFont['tag'];
    font: ThemeFont['font'];
} & Record<ThemeFontProperty, ThemeResponsive<string | number | null>>;

function readStored(property: ThemeFontProperty, value: string | number | ThemeCssSize | undefined) {
    if (value === undefined) {
        return undefined;
    }

    return UNIT_BY_PROPERTY[property] ? (value as ThemeCssSize).value : (value as string | number);
}

function writeStored(property: ThemeFontProperty, value: string | number | undefined) {
    const unit = UNIT_BY_PROPERTY[property];

    if (!unit) {
        return value === null ? undefined : value;
    }

    const numeric = typeof value === 'number' ? value : Number.parseFloat(String(value));

    if (!Number.isFinite(numeric)) {
        return undefined;
    }

    return { value: CLAMP_BY_PROPERTY[property]!(numeric), unit };
}

function mapResponsive<In, Out>(
    responsive: ThemeResponsive<In> | undefined | null,
    map: (value: In) => Out | undefined
): ThemeResponsive<Out> | undefined {
    if (!responsive) {
        return undefined;
    }

    const mapped: ThemeResponsive<Out> = {};

    (Object.keys(responsive) as ThemeBreakpoint[]).forEach((breakpoint) => {
        const value = responsive[breakpoint];
        const next = value === undefined ? undefined : map(value);

        if (next !== undefined) {
            mapped[breakpoint] = next;
        }
    });

    return Object.keys(mapped).length > 0 ? mapped : undefined;
}

/**
 * Stored font → form value.
 *
 * **Every breakpoint is present, holding the value it inherits where the font stores none**, and
 * `null` only where nothing resolves. A signal form derives its field tree from the value, so a
 * breakpoint missing from the object has no field — and the panel asks for all three on every
 * render. The stored font is compact (a synchronised one keeps desktop alone), so the tabs show
 * what the page renders, and turning synchronisation off starts every tab from that.
 */
export function toFontFormValue(font: ThemeFont): FontFormValue {
    const value = { tag: font.tag, font: font.font } as FontFormValue;

    THEME_FONT_PROPERTIES.forEach((property) => {
        const stored = font[property] as ThemeResponsive<string | number | ThemeCssSize> | undefined;
        const mapped = mapResponsive(stored, (item) => readStored(property, item));

        value[property] = {
            desktop: resolveBreakpoint(mapped, 'desktop') ?? null,
            tablet: resolveBreakpoint(mapped, 'tablet') ?? null,
            mobile: resolveBreakpoint(mapped, 'mobile') ?? null,
        };
    });

    return value;
}

/** Form value → stored font. */
export function fromFontFormValue(value: FontFormValue): ThemeFont {
    const font = { tag: value.tag, font: value.font } as ThemeFont;

    THEME_FONT_PROPERTIES.forEach((property) => {
        (font as Record<string, unknown>)[property] = mapResponsive(value[property], (edited) =>
            writeStored(property, edited)
        );
    });

    return font;
}
